import { useState } from 'react';
import {
    NormalButton,
    HollowedButton,
    TintedButton,
    GrayButton,
    GhostButton,
    RefreshButton
} from '@components/General/Button';

import { designSystem } from '@styles/designSystem';
import { Subtitle, H1Title, H2Title } from '@components/General/Titles';
import { HorizontalContainer, HorizontalSpacer, VerticalContainer, VerticalMainContainer } from '@components/General/Containers';

const ButtonShowcaseScreen = () => {
    const [loading, setLoading] = useState(false);
    const [clickedButton, setClickedButton] = useState<string>('');

    const handleClick = (buttonName: string) => {
        console.log(`${buttonName} clicked`);
        setClickedButton(buttonName);
        setTimeout(() => setClickedButton(''), 1000);
    };

    const handleLoadingClick = () => {
        console.log('Loading button clicked');
        setLoading(true);
        setTimeout(() => setLoading(false), 3000);
    };

    const handleRefresh = () => {
        console.log('Refresh clicked');
    };

    return (
        <VerticalContainer>
            {/* Header */}
            <VerticalContainer gap={designSystem.gapNone}>
                <H1Title 
                    text="Button Component Showcase"
                />
                <Subtitle
                    text="All button variants using CSS variables with full autocomplete support"
                />
            </VerticalContainer>

            {/* Last Clicked Info */}
            {clickedButton && (
                <div style={{
                    padding: designSystem.paddingMd,
                    backgroundColor: designSystem.colorItemClickedBackground,
                    borderRadius: designSystem.radiusButton,
                    color: designSystem.colorMainAction,
                    fontWeight: 600
                }}>
                    Last clicked: {clickedButton}
                </div>
            )}

            {/* Normal Button */}
            <section>
                <VerticalContainer>
                    <H2Title
                        text="Normal Button"
                    />
                    <HorizontalContainer>
                        <NormalButton 
                            text="Primary" 
                            onClick={() => handleClick('Normal Button')} 
                        />
                        <NormalButton 
                            text="Disabled" 
                            onClick={() => handleClick('Disabled')} 
                            disabled 
                        />
                        <NormalButton 
                            text="Loading" 
                            onClick={handleLoadingClick} 
                            loading={loading}
                        />
                        <HorizontalSpacer/>
                        <HorizontalContainer>
                            <NormalButton 
                                text="Expanded" 
                                onClick={() => handleClick('Normal Button')}
                                width={'100%'}
                            />                    
                        </HorizontalContainer>
                    </HorizontalContainer>
                    <div style={{ 
                        display: 'flex', 
                        gap: designSystem.gapMd, 
                        flexWrap: 'wrap' 
                    }}>
                        <div style={{ width: designSystem.sizeButtonWidth }}>
                            <NormalButton 
                                text="Primary" 
                                onClick={() => handleClick('Normal Button')} 
                            />
                        </div>
                        <div style={{ width: designSystem.sizeButtonWidth }}>
                            <NormalButton 
                                text="Disabled" 
                                onClick={() => handleClick('Disabled')} 
                                disabled 
                            />
                        </div>
                        <div style={{ width: designSystem.sizeButtonWidth }}>
                            <NormalButton 
                                text="Loading" 
                                onClick={handleLoadingClick} 
                                loading={loading}
                            />
                        </div>
                    </div>
                </VerticalContainer>
            </section>

            {/* Hollowed Button */}
            <section>
                <h2 style={{ 
                    fontSize: designSystem.fontSizeTitle, 
                    marginBottom: designSystem.marginMd,
                    color: designSystem.colorTitleText
                }}>
                    Hollowed Button
                </h2>
                <div style={{ 
                    display: 'flex', 
                    gap: designSystem.gapMd, 
                    flexWrap: 'wrap' 
                }}>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <HollowedButton 
                            text="Outline" 
                            onClick={() => handleClick('Hollowed Button')} 
                        />
                    </div>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <HollowedButton 
                            text="Disabled" 
                            onClick={() => handleClick('Disabled')} 
                            disabled 
                        />
                    </div>
                </div>
            </section>

            {/* Tinted Button */}
            <section>
                <h2 style={{ 
                    fontSize: designSystem.fontSizeTitle, 
                    marginBottom: designSystem.marginMd,
                    color: designSystem.colorTitleText
                }}>
                    Tinted Button
                </h2>
                <div style={{ 
                    display: 'flex', 
                    gap: designSystem.gapMd, 
                    flexWrap: 'wrap' 
                }}>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <TintedButton 
                            text="Tinted" 
                            onClick={() => handleClick('Tinted Button')} 
                        />
                    </div>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <TintedButton 
                            text="Disabled" 
                            onClick={() => handleClick('Disabled')} 
                            disabled 
                        />
                    </div>
                </div>
            </section>

            {/* Gray Button */}
            <section>
                <h2 style={{ 
                    fontSize: designSystem.fontSizeTitle, 
                    marginBottom: designSystem.marginMd,
                    color: designSystem.colorTitleText
                }}>
                    Gray Button
                </h2>
                <div style={{ 
                    display: 'flex', 
                    gap: designSystem.gapMd, 
                    flexWrap: 'wrap' 
                }}>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <GrayButton 
                            text="Secondary" 
                            onClick={() => handleClick('Gray Button')} 
                        />
                    </div>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <GrayButton 
                            text="Disabled" 
                            onClick={() => handleClick('Disabled')} 
                            disabled 
                        />
                    </div>
                </div>
            </section>

            {/* Ghost Button */}
            <section>
                <h2 style={{ 
                    fontSize: designSystem.fontSizeTitle, 
                    marginBottom: designSystem.marginMd,
                    color: designSystem.colorTitleText
                }}>
                    Ghost Button
                </h2>
                <div style={{ 
                    display: 'flex', 
                    gap: designSystem.gapMd, 
                    flexWrap: 'wrap' 
                }}>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <GhostButton 
                            text="Ghost" 
                            onClick={() => handleClick('Ghost Button')} 
                        />
                    </div>
                    <div style={{ width: designSystem.sizeButtonWidth }}>
                        <GhostButton 
                            text="Disabled" 
                            onClick={() => handleClick('Disabled')} 
                            disabled 
                        />
                    </div>
                </div>
            </section>

            {/* Refresh Button */}
            <section>
                <H2Title
                    text={"Refresh Button"}
                />
                <HorizontalContainer>
                    <RefreshButton 
                        text="" 
                        onClick={handleRefresh} 
                    />
                    <p>
                        Click to see rotation animation
                    </p>                            
                </HorizontalContainer>
            </section>

            {/* CSS Variables Preview */}
            <section>
                <h2 style={{ 
                    fontSize: designSystem.fontSizeTitle, 
                    marginBottom: designSystem.marginMd,
                    color: designSystem.colorTitleText
                }}>
                    CSS Variables Preview
                </h2>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: designSystem.gapSm,
                    backgroundColor: 'white',
                    padding: designSystem.paddingLg,
                    borderRadius: designSystem.radiusButton
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.gapMd }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: designSystem.colorMainAction,
                            borderRadius: designSystem.radiusButton
                        }} />
                        <div>
                            <div style={{ fontWeight: 600 }}>colorMainAction</div>
                            <div style={{ fontSize: '14px', color: designSystem.colorWarningText }}>#EC6C56</div>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.gapMd }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: designSystem.colorDropdownBackground,
                            borderRadius: designSystem.radiusButton,
                            border: '1px solid #ddd'
                        }} />
                        <div>
                            <div style={{ fontWeight: 600 }}>colorDropdownBackground</div>
                            <div style={{ fontSize: '14px', color: designSystem.colorWarningText }}>#F9F9F9</div>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.gapMd }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: designSystem.colorDropdownText,
                            borderRadius: designSystem.radiusButton
                        }} />
                        <div>
                            <div style={{ fontWeight: 600 }}>colorDropdownText</div>
                            <div style={{ fontSize: '14px', color: designSystem.colorWarningText }}>#666</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.gapMd }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: designSystem.colorItemClickedBackground,
                            borderRadius: designSystem.radiusButton,
                            border: '1px solid #ddd'
                        }} />
                        <div>
                            <div style={{ fontWeight: 600 }}>colorItemClickedBackground</div>
                            <div style={{ fontSize: '14px', color: designSystem.colorWarningText }}>#F3F4FD</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Typography Showcase */}
            <section>
                <h2 style={{ 
                    fontSize: designSystem.fontSizeTitle, 
                    marginBottom: designSystem.marginMd,
                    color: designSystem.colorTitleText
                }}>
                    Typography Scale
                </h2>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: designSystem.gapSm,
                    backgroundColor: 'white',
                    padding: designSystem.paddingLg,
                    borderRadius: designSystem.radiusButton
                }}>
                    <div style={{ fontSize: designSystem.fontSizeLg, fontWeight: 700 }}>
                        Large (32px) - cssVar.fontSizeLg
                    </div>
                    <div style={{ fontSize: designSystem.fontSizeMd, fontWeight: 600 }}>
                        Medium (24px) - cssVar.fontSizeMd
                    </div>
                    <div style={{ fontSize: designSystem.fontSizeSm, fontWeight: 400 }}>
                        Small (16px) - cssVar.fontSizeSm
                    </div>
                    <div style={{ fontSize: designSystem.fontSizeDropdownText, fontWeight: 700, color: designSystem.colorDropdownText }}>
                        Dropdown (13px) - cssVar.fontSizeDropdownText
                    </div>
                </div>
            </section>
        </VerticalContainer>
    );
};

export default ButtonShowcaseScreen;